import React, { useRef } from 'react';
import styles from './AssetOpenedTickets.module.scss';

import { Paper, Table, TableBody, TableContainer, TableHead, TableRow } from '@material-ui/core';

import { StyledTableCell, StyledTableRow } from 'components/StyledComponents';
import LinkComponent from 'components/LinkComponent/LinkComponent';
import TablePaginationRefactored from 'components/TablePaginationRefactored/TablePaginationRefactored';
import StatusIndicator from 'components/StatusIndicator';
import StyledTooltip from 'components/StyledTooltip';

import { TICKET_DETAILED_PATH } from 'constants/routeConstants';

import { dateTimeFormat, formatDate, getUserFullName } from 'helpers/AppHelpers';
import { getChangedByPageFilter } from 'helpers/SummaryPagesHelpers';

import { ASSET_OPENED_TICKETS_TABLE_PARAMETER } from 'constants/configTableConstants';
import { TOOLTIP_ID } from 'constants/commonConstants';

import { useMobileViewport } from 'hooks/useMobileViewport';
import { useUserConfig } from 'hooks/useUserConfig';
import { useAssetActions, useAssetSelector } from 'hooks/Asset';

export default function AssetOpenedTickets() {
  const isMobile = useMobileViewport();
  const tableRef = useRef(null);

  const { getTableLimit } = useUserConfig();

  const { openedTickets, openedTicketsFilter } = useAssetSelector();
  const { getOpenedTickets } = useAssetActions();

  const emptyResponse = () =>
    !openedTickets?.items?.length &&
    openedTicketsFilter?.pagination?.page === openedTickets?.pageNumber;

  const changePage = (page, param) =>
    getOpenedTickets(getChangedByPageFilter(openedTicketsFilter, page), param);

  const getUpdatedByLimitFilter = () => ({
    ...openedTicketsFilter,
    pagination: {
      ...openedTicketsFilter.pagination,
      page: 1,
      limit: getTableLimit(ASSET_OPENED_TICKETS_TABLE_PARAMETER)
    },
    isDataReceived: false
  });

  return (
    <>
      <StyledTooltip />

      <TableContainer className={styles.table} component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <StyledTableCell>Asset</StyledTableCell>
              <StyledTableCell>ID</StyledTableCell>
              <StyledTableCell>Type</StyledTableCell>
              <StyledTableCell>Severity</StyledTableCell>
              <StyledTableCell>Description</StyledTableCell>
              <StyledTableCell>Opened By</StyledTableCell>
              <StyledTableCell>Opened On</StyledTableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {openedTickets?.items?.length ? (
              openedTickets.items.map((row, index) => (
                <StyledTableRow key={index}>
                  <StyledTableCell>{row.asset?.drCode || ''}</StyledTableCell>
                  <StyledTableCell>
                    <LinkComponent
                      name={row.id}
                      path={`${TICKET_DETAILED_PATH}/${row.id}`}
                      isRedirectAvailable={!row.deleted}
                      boldText
                    />
                  </StyledTableCell>
                  <StyledTableCell>{row.type}</StyledTableCell>
                  <StyledTableCell>
                    <StatusIndicator status={row.severity?.name} />
                  </StyledTableCell>
                  <StyledTableCell
                    data-tooltip-id={TOOLTIP_ID}
                    data-tooltip-content={row.description}>
                    {row.description}
                  </StyledTableCell>
                  <StyledTableCell>{getUserFullName(row['openedByUser'])}</StyledTableCell>
                  <StyledTableCell>
                    {row['openedAtUtc'] ? formatDate(row['openedAtUtc'], dateTimeFormat) : ''}
                  </StyledTableCell>
                </StyledTableRow>
              ))
            ) : (
              <StyledTableRow>
                <StyledTableCell colSpan={10} className={styles.empty}>
                  No available data in the table.
                </StyledTableCell>
              </StyledTableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <div className={styles.footer}>
        <TablePaginationRefactored
          isMobile={isMobile}
          rows={openedTickets?.items?.length}
          currentPage={openedTicketsFilter?.pagination?.page}
          totalPages={openedTicketsFilter?.pagination?.totalPages}
          selectAllOption
          onPageSelect={changePage}
          pageLimit={openedTicketsFilter?.pagination?.limit}
          hide={emptyResponse()}
          tableListParameter={ASSET_OPENED_TICKETS_TABLE_PARAMETER}
          getTableData={() => getOpenedTickets(getUpdatedByLimitFilter())}
          tableRef={tableRef}
        />
      </div>
    </>
  );
}
